import React, { useEffect, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';

import { AddButton } from 'components/Button';
import { Search } from 'components/Search';

import { useLevelStructures } from '../api/getLevelStructures';
import { useDeleteRole } from '../api/deleteRole';
import { useRoles } from '../api/getRoles';
import { useReorderRoles } from '../api/reorderRoles';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { RoleFormModal } from './RoleFormModal';
import { RoleGroup } from './RoleGroup';

export const RolesView = ({ resource, id, lot, onChanged, onModalChange }) => {
  const [editedRole, setEditedRole] = useState(null);
  const [deletedRole, setDeletedRole] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    onModalChange(!!editedRole || !!deletedRole);
  }, [editedRole, deletedRole, onModalChange]);

  const lotId = lot.lot_id;
  const { data: roles, refetch, isFetchedAfterMount } = useRoles(resource, id, lotId);
  const { data: structures, isFetchedAfterMount: areStructuresFetched } = useLevelStructures(
    resource,
    id
  );

  const refresh = () => {
    refetch();
    onChanged();
  };
  const { mutateAsync: deleteRole } = useDeleteRole(resource, id, () => {
    refresh();
    setDeletedRole(null);
  });
  const { mutateAsync: reorderRoles } = useReorderRoles(resource, id, refetch);

  const searchTerm = search.trim().toLowerCase();
  const filteredRoles = roles.filter(
    ({ name, description }) =>
      name.toLowerCase().includes(searchTerm) || description.toLowerCase().includes(searchTerm)
  );
  const groups = structures.filter(({ level_structure_id }) =>
    filteredRoles.some((role) => role.level_structure_id === level_structure_id)
  );

  const hasNoStructures = areStructuresFetched && structures.length === 0;
  const emptyState = hasNoStructures
    ? ['No level structures yet', 'Add a level structure before adding roles']
    : searchTerm
      ? ['No roles found', 'Please try another search term']
      : ['No roles yet', 'Click "Add role" to begin'];

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Search
          handleSearchChange={({ target: { value } }) => setSearch(value)}
          sx={{ minWidth: 200, width: 270 }}
        />
        {structures.length > 0 && (
          <AddButton
            onClick={() => setEditedRole({})}
            sx={({ palette }) => ({
              svg: { mt: -0.5 },
              minWidth: 200,
              border: `1px solid ${palette.grey[200]}`,
            })}
            color="secondary"
          >
            Add role
          </AddButton>
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {isFetchedAfterMount && areStructuresFetched && groups.length === 0 && (
        <Stack flex={1} alignItems="center" justifyContent="center" gap={1}>
          <Typography variant="h5" fontWeight={700}>
            {emptyState[0]}
          </Typography>
          <Typography color="text.secondary">{emptyState[1]}</Typography>
        </Stack>
      )}

      <Stack gap={3}>
        {groups.map((structure) => (
          <RoleGroup
            key={structure.level_structure_id}
            structure={structure}
            roles={roles}
            searchTerm={searchTerm}
            onReorder={(levelStructureId, roleIds) =>
              reorderRoles({ lotId, levelStructureId, roleIds })
            }
            onEdit={setEditedRole}
            onDelete={setDeletedRole}
          />
        ))}
      </Stack>

      <RoleFormModal
        resource={resource}
        id={id}
        lotId={lotId}
        role={editedRole}
        structures={structures}
        open={!!editedRole}
        handleClose={() => setEditedRole(null)}
        onSaved={refresh}
      />

      <DeleteConfirmModal
        title="Delete role"
        label="role"
        name={deletedRole?.name}
        open={!!deletedRole}
        handleClose={() => setDeletedRole(null)}
        onConfirm={() => deleteRole({ lotId, roleId: deletedRole.role_id }).catch(() => {})}
      />
    </>
  );
};
